import { Participant } from '../models';
import { MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { logger } from '@/src/core/utils';

export interface LeaderboardEntry {
  participant: Participant;
  position: number;
  wins: number;
  losses: number;
  draws: number;
  points?: number; // Para Round Robin
  totalMatches: number;
  winRate: number;
  status: 'champion' | 'finalist' | 'semifinalist' | 'quarterfinalist' | 'eliminated' | 'active';
}

export class GetBracketLeaderboardUseCase {
  constructor(
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository,
    private tournamentRepository: TournamentRepository
  ) {}

  async execute(tournamentId: string): Promise<LeaderboardEntry[]> {
    logger.debug('GetBracketLeaderboard', 'Generating leaderboard', { tournamentId });

    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    const matches = await this.matchRepository.getByTournamentId(tournamentId);

    // Calcular estadísticas para cada participante
    const entries: LeaderboardEntry[] = participants.map(participant => {
      let wins = 0;
      let losses = 0;
      let draws = 0;
      let points = 0;

      // Contar victorias, derrotas, empates
      const participantMatches = matches.filter(
        m => m.participant1Id === participant.id || m.participant2Id === participant.id
      );

      participantMatches.forEach(match => {
        if (match.status === 'completed') {
          if (match.winnerId === participant.id) {
            wins++;
            if (tournament.tournamentType === 'round_robin') {
              points += 3; // 3 puntos por victoria
            }
          } else if (match.winnerId === null) {
            draws++;
            if (tournament.tournamentType === 'round_robin') {
              points += 1; // 1 punto por empate
            }
          } else if (match.winnerId) {
            losses++;
          }
        }
      });

      const totalMatches = wins + losses + draws;
      const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

      return {
        participant,
        position: 0, // Se calculará después
        wins,
        losses,
        draws,
        points: tournament.tournamentType === 'round_robin' ? points : undefined,
        totalMatches,
        winRate,
        status: 'active' as const,
      };
    });

    // Ordenar según el tipo de torneo
    if (tournament.tournamentType === 'round_robin') {
      // Ordenar por puntos, luego por victorias, luego por win rate
      entries.sort((a, b) => {
        if (b.points !== a.points) return (b.points || 0) - (a.points || 0);
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.winRate - a.winRate;
      });
    } else if (tournament.tournamentType === 'king_of_the_hill') {
      // King of the Hill: rank by elimination round (later = better), champion first
      const lastMatch = matches.find(m => !m.nextMatchId && m.status === 'completed');
      const championId = lastMatch?.winnerId;
      const eliminationRound = new Map<string, number>();
      for (const m of matches) {
        if (m.status === 'completed' && m.winnerId) {
          const loserId = m.participant1Id === m.winnerId ? m.participant2Id : m.participant1Id;
          if (loserId) eliminationRound.set(loserId, m.round);
        }
      }
      entries.sort((a, b) => {
        if (a.participant.id === championId) return -1;
        if (b.participant.id === championId) return 1;
        return (eliminationRound.get(b.participant.id) ?? 0) - (eliminationRound.get(a.participant.id) ?? 0);
      });
    } else {
      // Para eliminación: ordenar por victorias y win rate
      entries.sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.winRate - a.winRate;
      });
    }

    // Asignar posiciones
    entries.forEach((entry, index) => {
      entry.position = index + 1;

      // Determinar status para torneos completados
      if (tournament.status === 'completed') {
        if (index === 0) entry.status = 'champion';
        else if (index === 1) entry.status = 'finalist';
        else if (index < 4) entry.status = 'semifinalist';
        else if (index < 8) entry.status = 'quarterfinalist';
        else entry.status = 'eliminated';
      }
    });

    logger.debug('GetBracketLeaderboard', 'Leaderboard generated', { count: entries.length });

    return entries;
  }
}
